/**
 * SUSI — <fetched-content>
 *
 * Fills itself with markup rendered elsewhere, the moment it comes near the viewport. Used for
 * product recommendations (related and complementary): the same section is requested through the
 * recommendations endpoint and the matching element of the response is copied in.
 *
 * Attributes:
 *   data-url        URL to request (e.g. {{ routes.product_recommendations_url }}?section_id=…&product_id=…&intent=related)
 *   data-fetch-id   The element with the same data-fetch-id in the response supplies the content
 *   data-selector   Alternative: any CSS selector to look up in the response
 *   data-eager      Fetch right away instead of waiting for the element to scroll near
 * State:  .is-loading while waiting, [data-loaded] afterwards; stays [hidden] when nothing came back
 * Event:  on:fetched-content:load { empty: boolean } (bubbles)
 * API:    load() → Promise, reload()
 */
(() => {
  if (customElements.get('fetched-content')) return;

  const theme = window.theme;

  class FetchedContent extends HTMLElement {
    connectedCallback() {
      if (this.started || !this.dataset.url) return;
      // Inside the theme editor recommendations are requested for a placeholder product; still try
      if (this.hasAttribute('data-eager')) {
        this.load();
      } else {
        this.watcher = theme.onVisible(this, () => this.load(), { rootMargin: '600px 0px' });
      }
    }

    disconnectedCallback() {
      if (this.watcher) this.watcher.disconnect();
      if (this.request) this.request.abort();
    }

    reload() {
      this.started = false;
      return this.load();
    }

    async load() {
      if (this.started) return;
      this.started = true;
      this.classList.add('is-loading');
      this.request = new AbortController();

      let empty = true;
      try {
        const doc = await theme.fetchDocument(this.dataset.url, { signal: this.request.signal });
        const selector = this.dataset.selector || `[data-fetch-id="${CSS.escape(this.dataset.fetchId || '')}"]`;
        const source = doc.querySelector(selector);
        if (source && source.innerHTML.trim() !== '') {
          this.innerHTML = source.innerHTML;
          empty = false;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
      } finally {
        this.classList.remove('is-loading');
      }

      this.setAttribute('data-loaded', '');
      this.hidden = empty && this.innerHTML.trim() === '';
      theme.emit('on:fetched-content:load', { empty }, this);
    }
  }

  customElements.define('fetched-content', FetchedContent);
})();
